/**
 * Android Destination Provider — Storage Access Framework.
 *
 * Uses ACTION_CREATE_DOCUMENT via a native module to let the user pick
 * a save location through the Android system file picker.
 *
 * Does NOT use RNFS.ExternalStorageDirectoryPath — modern Android (10+)
 * requires SAF or app-private external storage. Direct environment paths
 * require MANAGE_EXTERNAL_STORAGE which Google Play rejects for most apps.
 */

import type { PlatformDestinationProvider } from './destination';
import type { TorrentDestination } from '../networking/engines/torrent/torrent-engine';

const NativeModules = (globalThis as any).NativeModules || {};
const { ShareFileSaver } = NativeModules;

export class AndroidDestinationProvider implements PlatformDestinationProvider {
  async chooseDestination(
    suggestedFileName: string
  ): Promise<TorrentDestination> {
    if (!ShareFileSaver) {
      return this.defaultDestination(suggestedFileName);
    }

    // ShareFileSaver.pickSaveLocation uses ACTION_CREATE_DOCUMENT
    // Returns an absolute path resolved from the content:// URI
    const path: string = await ShareFileSaver.pickSaveLocation(
      suggestedFileName
    );
    return { kind: 'native-path', path };
  }

  async defaultDestination(
    suggestedFileName: string
  ): Promise<TorrentDestination> {
    if (ShareFileSaver) {
      // App-private external files — no WRITE_EXTERNAL_STORAGE needed on Android 10+
      const base: string = await ShareFileSaver.getDefaultDownloadsPath();
      return { kind: 'native-path', path: `${base}/${suggestedFileName}` };
    }

    // Absolute fallback
    return {
      kind: 'native-path',
      path: `/sdcard/Download/${suggestedFileName}`,
    };
  }
}
